
import "./ReportAnalysisTab.css";

import { useState, useEffect } from "react";
import { MetricCard } from "./MetricCard";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Cell,
} from "recharts";
import { getAdminStatistics } from "../../../Servicefile/ReportAnalysisService";

const COLORS = ["#f97316", "#3b82f6", "#22c55e", "#a855f7", "#eab308", "#64748b"];

function RevenueTab({ data: propData, isLoading: propIsLoading }) {
    const [toggle, setToggle] = useState("monthly");
    const [data, setData] = useState(propData || null);
    const [loading, setLoading] = useState(!propData);
    const [error, setError] = useState(null);

    // Update data when propData changes (from filters)
    useEffect(() => {
        if (propData) {
            setData(propData);
            setLoading(false);
            setError(null);
        }
    }, [propData]);

    // Fetch data only if no propData is provided
    useEffect(() => {
        if (!propData) {
            fetchData();
        }
    }, [propData]);

    const fetchData = async () => {
        try {

            setLoading(true);
            setError(null);

            const result = await getAdminStatistics();

            setData(result);

        } catch (err) {

            if (
                err.message.includes("Failed to fetch") ||
                err.message.includes("Network")
            ) {

                setError(
                    "Unable to connect to the server. Please check your internet connection and try again."
                );

            } else if (
                err.message === "SESSION_EXPIRED"
            ) {

                setError(
                    "Your session has expired. Please log in again to continue."
                );

            } else if (
                err.message === "DATA_NOT_FOUND"
            ) {

                setError(
                    "Unable to load data. Please try again later."
                );

            } else {

                setError("Please try again later.");
            }

        } finally {

            setLoading(false);
        }
    };

    // Use propIsLoading if provided, otherwise use local loading state
    const isLoading = propIsLoading !== undefined ? propIsLoading : loading;

    if (isLoading) {
        return (
            <div className="tab-content flex justify-center items-center h-64">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
                    <p className="text-muted-foreground">Loading revenue data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="tab-content flex justify-center items-center h-64">
                <div className="text-center text-negative">
                    <p className="font-semibold mb-2">Unable to Load Data</p>
                    <p className="text-sm">{error}</p>

                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="tab-content flex justify-center items-center h-64">
                <p className="text-muted-foreground">No data available</p>
            </div>
        );
    }

    // Transform revenueByCategory object into array format for charts
    // Apply category filter if present
    let revenueByCategoryData = [];
    if (data.filteredByCategory && data.revenueByCategory) {
        // If filtered by category, only show that category
        revenueByCategoryData = [{
            category: data.filteredByCategory,
            value: data.filteredCategoryRevenue || 0
        }];
    } else {
        // Otherwise show all categories
        revenueByCategoryData = Object.entries(data.revenueByCategory || {}).map(([category, value]) => ({
            category,
            value
        }));
    }

    // Transform dailyStats into the format expected by the chart
    // Apply date range filter if present
    const revenueTrendData = (data.dailyStats || []).map(stat => ({
        date: new Date(stat.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
        revenue: stat.revenue,
        commission: data.platformCommission ? data.platformCommission / (data.dailyStats?.length || 1) : 0
    }));

    // Calculate totals with filters applied
    const totalRevenue = data.totalRevenue || 0;
    const totalCommission = data.platformCommission || 0;
    const avgOrderValue = data.avgOrderValue || 0;
    const revenueGrowth = data.revenueGrowthPercent || 0;

    // Get filtered payment amount if payment filter is applied
    const filteredPaymentAmount = data.filteredPaymentAmount;

    // Calculate meal period revenue with current data
    const breakfastRevenue = calculateMealPeriodRevenue(data.hourlyOrderBreakdown, [7, 8, 9, 10]) * (data.avgOrderValue || 0);
    const lunchRevenue = calculateMealPeriodRevenue(data.hourlyOrderBreakdown, [11, 12, 13, 14]) * (data.avgOrderValue || 0);
    const eveningRevenue = calculateMealPeriodRevenue(data.hourlyOrderBreakdown, [15, 16, 17, 18]) * (data.avgOrderValue || 0);
    const dinnerRevenue = calculateMealPeriodRevenue(data.hourlyOrderBreakdown, [19, 20, 21, 22]) * (data.avgOrderValue || 0);
    const lateNightRevenue = calculateMealPeriodRevenue(data.hourlyOrderBreakdown, [23, 0, 1, 2, 3, 4, 5, 6]) * (data.avgOrderValue || 0);

    return (
        <div className="tab-content space-y-5">
            {/* Filter Indicators */}
            {(data.filteredByStatus || data.filteredByPayment || data.filteredByCategory) && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-xs">
                    <span className="font-semibold text-primary">Active Filters: </span>
                    {data.filteredByStatus && (
                        <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded-full mr-2">
                            Status: {data.filteredByStatus} ({data.filteredValue} orders)
                        </span>
                    )}
                    {data.filteredByPayment && (
                        <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded-full mr-2">
                            Payment: {data.filteredByPayment} (₹{data.filteredPaymentAmount?.toLocaleString()})
                        </span>
                    )}
                    {data.filteredByCategory && (
                        <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded-full mr-2">
                            Category: {data.filteredByCategory} (₹{data.filteredCategoryRevenue?.toLocaleString()})
                        </span>
                    )}
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard title="Total Revenue" value={totalRevenue} prefix="₹" highlight />
                <MetricCard title="Revenue Growth %" value={`${revenueGrowth}%`} />
                <MetricCard title="Average Order Value" value={avgOrderValue} prefix="₹" />
                {/* <MetricCard title="Platform Commission" value={totalCommission} prefix="₹"  /> */}
            </div>


            {/* Payment Methods Breakdown - As Individual Cards */}
            {data.paymentBreakdown && Object.keys(data.paymentBreakdown).length > 0 && (
                <>
                    <h2>Payment Breakdown</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(data.paymentBreakdown).map(([method, amount], index) => {
                            // Format the method name
                            let formattedMethod = method.replace(/_/g, ' ');

                            if (method === "Maamaas_Wallet") {
                                formattedMethod = "Maamaas Wallet";
                            } else if (method === "ONLINE_PAYMENT") {
                                formattedMethod = "Online Payment";
                            } else if (method === "CASH") {
                                formattedMethod = "Cash";
                            } else if (method === "CARD") {
                                formattedMethod = "Card";
                            } else if (method === "UPI") {
                                formattedMethod = "UPI";
                            }

                            const percentage = totalRevenue > 0 ? (amount / totalRevenue) * 100 : 0;

                            // Choose icon based on payment method
                            let icon = "💳";
                            if (method === "CASH") icon = "💵";
                            if (method === "UPI") icon = "📱";
                            if (method === "Maamaas_Wallet") icon = "👛";
                            if (method === "CARD") icon = "💳";

                            return (
                                <MetricCard
                                    key={method}
                                    title={formattedMethod}
                                    value={amount}
                                    prefix="₹"
                                // growth={percentage}
                                // prevValue={`${percentage.toFixed(1)}% of total`}
                                // icon={icon}
                                />
                            );
                        })}
                    </div>
                </>
            )}

            {/* Revenue Trend */}
            <div className="dash-card p-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Revenue Trend</h3>
                    <div className="flex bg-muted rounded-lg p-1 gap-1">
                        {["daily", "weekly", "monthly"].map((t) => (
                            <button
                                key={t}
                                onClick={() => setToggle(t)}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${toggle === t ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                                    }`}
                            >
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={revenueTrendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 13% 91%)" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => [`₹${v.toLocaleString()}`, ""]} />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={2.5} dot={{ r: 4 }} name="Revenue" />
                        <Line type="monotone" dataKey="commission" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="Commission" strokeDasharray="5 5" />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
                {/* Revenue by Category */}
                <div className="dash-card p-5">
                    <h3 className="font-semibold mb-4">Revenue by Category</h3>
                    {revenueByCategoryData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={revenueByCategoryData} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(220 13% 91%)" />
                                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                                <YAxis dataKey="category" type="category" tick={{ fontSize: 11 }} width={90} />
                                <Tooltip formatter={(v) => [`₹${v.toLocaleString()}`, "Revenue"]} />
                                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                    {revenueByCategoryData.map((entry, i) => (
                                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-[220px] text-muted-foreground">
                            No category data available
                        </div>
                    )}
                </div>

                {/* Refund Impact - Using actual refund data */}
                <div className="dash-card p-5">
                    <h3 className="font-semibold mb-4">Wastage & Spoilage Impact</h3>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={[
                            { month: "Sep", wastage: data.refundAmount ? data.refundAmount * 0.6 : 18200, spoilage: data.refundAmount ? data.refundAmount * 0.4 : 14100 },
                            { month: "Oct", wastage: 22400, spoilage: 18600 },
                            { month: "Nov", wastage: 19800, spoilage: 21200 },
                            { month: "Dec", wastage: 24600, spoilage: 25800 },
                        ]}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 13% 91%)" />
                            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => [`₹${v.toLocaleString()}`, ""]} />
                            <Legend />
                            <Bar dataKey="wastage" fill="#ef4444" name="Wastage" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="spoilage" fill="#f97316" name="Spoilage" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Meal Period Revenue - Using hourly data to calculate meal periods */}
            <div className="dash-card p-5">
                <h3 className="font-semibold mb-4">Revenue by Meal Period</h3>
                <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={[
                        { period: "Breakfast", revenue: breakfastRevenue },
                        { period: "Lunch", revenue: lunchRevenue },
                        { period: "Evening", revenue: eveningRevenue },
                        { period: "Dinner", revenue: dinnerRevenue },
                        { period: "Late Night", revenue: lateNightRevenue },
                    ]}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 13% 91%)" />
                        <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => [`₹${v.toLocaleString()}`, "Revenue"]} />
                        <Bar dataKey="revenue" fill="#f97316" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
} export default RevenueTab;

// Helper function to calculate orders for specific hours
function calculateMealPeriodRevenue(hourlyData, hours) {
    if (!hourlyData) return 0;
    return hourlyData
        .filter(item => hours.includes(parseInt(item.hour.split(':')[0])))
        .reduce((sum, item) => sum + item.orders, 0);
} 